import { ImageFile } from 'domain/File';

import { assert } from './assert';

export const getFileNameFromFilePath = (filePath: string): string => {
  const pathElements = filePath.split('\\');
  const lastIndex = pathElements.length - 1;
  return pathElements[lastIndex];
};

export const dataURItoFile = (dataURI: string, fileName: string): File => {
  const type = dataURI.split(';base64,')[0].split(':')[1];
  const bytes = atob(dataURI.split(';base64,')[1]);
  const data = new Uint8Array(new ArrayBuffer(bytes.length));

  for (let i = 0; i < bytes.length; i++) {
    data[i] = bytes.charCodeAt(i);
  }

  return new File([data.buffer], fileName, { type });
};

export type CsvCell = { toString: () => string };
export type CsvRow = CsvCell[];

export const stringifyCsvData = (data: CsvRow[]) => data.map(sanitizeRow).join('\n');

const sanitizeRow = (row: CsvRow) => row.map(sanitizeCell).join(',');

const sanitizeCell = (cell: CsvCell) => (typeof cell === 'number' ? cell : `"${cell.toString().replace(/"/g, "'")}"`);

export const dataToCsvFile = (data: BlobPart, fileName: string): void => {
  try {
    dataToFile(data, fileName, 'text/csv;charset=utf-8;');
  } catch {
    console.error('Unable to convert data to CSV file');
  }
};

export const dataToFile = (data: BlobPart, fileName: string, type?: string): void => {
  const url =
    typeof data === 'string' && data.startsWith('data:')
      ? data
      : window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  a.click();
  a.remove();
};

export const blobToData = (blob: Blob): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(`${reader.result}`);
    };
    reader.readAsDataURL(blob);
  });

export const getImageFormData = <I extends { data?: string; name?: string } = ImageFile>(
  imageData: I,
  name = 'image'
) => {
  assert(imageData.data && imageData.name);

  const imageFileName = getFileNameFromFilePath(imageData.name);
  const imageFile = dataURItoFile(imageData.data, imageFileName);
  const imageFormData = new FormData();
  imageFormData.append(name, imageFile);

  return imageFormData;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const downloadBlob = async (link: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.open('GET', link);
    xhr.onload = () => {
      resolve(xhr.response);
    };
    xhr.onerror = reject;
    xhr.onabort = reject;
    xhr.send();
  });
